// ---------------------------------------------------------------------------
// Plain-English behavior description templates.
//
// These are one-tap description starters for the "Log an incident" form.
// They exist because "operational description" is a required FBA/BIP field,
// but a lot of teachers — especially teachers new to writing FBA data —
// freeze at the empty textbox. Giving them 3-4 concrete, editable
// examples per behavior type unblocks that step in one tap.
//
// Templates are written in the observable-and-measurable style FBA reviewers
// look for (what you could see or count), NOT in interpretive language
// ("Sam was frustrated"). Every template is generic and can be edited to
// fit the specific student and situation before saving.
//
// Placeholders:
//   {name} — replaced with the student's first name (falls back to "the student")
// ---------------------------------------------------------------------------

const BEHAVIOR_DESCRIPTION_TEMPLATES = {
  'non-compliance': [
    '{name} refused to start the assigned task after being asked twice.',
    '{name} put their head down and did not respond to teacher directions for about 5 minutes.',
    '{name} said "no" and pushed the materials away when asked to begin the task.',
  ],
  'verbal-aggression': [
    '{name} yelled "shut up" at a peer sitting next to them.',
    '{name} called a peer a name and used a raised voice.',
    '{name} shouted at the teacher when told to stop.',
  ],
  'physical-aggression': [
    '{name} hit a peer once on the arm with an open hand.',
    '{name} kicked the leg of a peer\'s chair 3 times.',
    '{name} pushed a peer with two hands, knocking them into the wall.',
  ],
  'self-injury': [
    '{name} hit their own head with an open hand 4 times.',
    '{name} bit their own forearm hard enough to leave a mark.',
    '{name} pulled their own hair for about 10 seconds.',
  ],
  'property-destruction': [
    '{name} threw their iPad on the floor 3 out of 5 times when asked to do work.',
    '{name} ripped their worksheet in half and threw it across the desk.',
    '{name} swept books and pencils off the desk with one arm.',
  ],
  disruption: [
    '{name} called out answers 6 times during a 15-minute lesson without being called on.',
    '{name} made loud vocal sounds ("ahh", "oh") repeatedly during independent work.',
    '{name} tapped a pencil loudly on the desk and did not stop when reminded.',
  ],
  elopement: [
    '{name} walked out of the classroom without permission and went to the hallway.',
    '{name} left their assigned seat and ran to the door.',
    '{name} left the small-group table and hid under a nearby desk.',
  ],
  'inappropriate-language': [
    '{name} used a curse word at conversational volume during class.',
    '{name} used a curse word directed at a peer in a raised voice.',
    '{name} used an inappropriate word repeatedly (about 4 times) after a redirection.',
  ],
  stereotypy: [
    '{name} rocked back and forth in their chair for about 3 minutes.',
    '{name} flapped their hands near their face for about 30 seconds.',
    '{name} repeated the same word out loud about 8 times in a row.',
  ],
  crying: [
    '{name} began crying and continued for about 4 minutes.',
    '{name} cried and put their head down on the desk when the task was presented.',
    '{name} whimpered and covered their face with their hands.',
  ],
  withdrawal: [
    '{name} pulled their hood up, turned away from the group, and did not respond to prompts.',
    '{name} put their head on the desk and stopped responding to peers or staff.',
    '{name} moved to the corner of the room and would not participate.',
  ],
  other: [
    '{name} engaged in a behavior that lasted about 2 minutes. See notes.',
    '{name} responded in a way that was outside classroom expectations. See notes.',
  ],
};

// Fill in {name} placeholders with the student's first name (or a generic
// fallback if we don't have it yet).
export function describeBehavior(behaviorType, studentFirstName) {
  const templates = BEHAVIOR_DESCRIPTION_TEMPLATES[behaviorType] || [];
  const name = (studentFirstName && studentFirstName.trim()) || 'The student';
  return templates.map((t) => t.replace(/\{name\}/g, name));
}

// Plain-English quick-pick behaviors used by the incident form's Simple
// mode. `value` matches BEHAVIOR_TYPE_OPTIONS.value so downstream analytics
// don't need to remap. `plainLabel` is the words the teacher sees on the
// big chip button.
export const QUICK_BEHAVIOR_CHIPS = [
  { value: 'non-compliance',        plainLabel: 'Refused / didn\'t follow direction', icon: 'XCircle',      color: 'amber'  },
  { value: 'verbal-aggression',     plainLabel: 'Yelled at someone',                 icon: 'MessageSquare',color: 'orange' },
  { value: 'physical-aggression',   plainLabel: 'Hit, kicked, pushed',               icon: 'Hand',         color: 'red'    },
  { value: 'property-destruction',  plainLabel: 'Threw / broke something',           icon: 'Package',      color: 'red'    },
  { value: 'elopement',             plainLabel: 'Left the room / ran off',           icon: 'LogOut',       color: 'orange' },
  { value: 'disruption',            plainLabel: 'Called out / disrupted class',      icon: 'AlertTriangle',color: 'amber'  },
  { value: 'self-injury',           plainLabel: 'Hurt themselves',                   icon: 'AlertOctagon', color: 'red'    },
  { value: 'crying',                plainLabel: 'Cried / very upset',                icon: 'CloudRain',    color: 'blue'   },
  { value: 'withdrawal',            plainLabel: 'Shut down / withdrew',              icon: 'Moon',         color: 'blue'   },
  { value: 'inappropriate-language',plainLabel: 'Used inappropriate language',       icon: 'MessageCircle',color: 'orange' },
  { value: 'stereotypy',            plainLabel: 'Repetitive movement / vocals',      icon: 'Repeat',       color: 'violet' },
  { value: 'other',                 plainLabel: 'Something else',                    icon: 'HelpCircle',   color: 'gray'   },
];

// Plain-English quick-pick antecedents — same idea as behaviors, but for
// the "what came before" step. Values match ANTECEDENT_OPTIONS.value.
export const QUICK_ANTECEDENT_CHIPS = [
  { value: 'task-demand',           plainLabel: 'Was asked to do a task' },
  { value: 'told-no',               plainLabel: 'Was told "no" or "stop"' },
  { value: 'denied-access',         plainLabel: 'Couldn\'t have something they wanted' },
  { value: 'transition',            plainLabel: 'Was changing to a new activity' },
  { value: 'waiting',               plainLabel: 'Was waiting / had to take turns' },
  { value: 'peer-conflict',         plainLabel: 'Had a problem with another kid' },
  { value: 'attention-to-others',   plainLabel: 'Adult was busy with another student' },
  { value: 'corrective-feedback',   plainLabel: 'Was corrected for something' },
  { value: 'sensory-overload',      plainLabel: 'Room was loud / busy / bright' },
  { value: 'schedule-change',       plainLabel: 'Schedule changed unexpectedly' },
  { value: 'non-preferred-activity',plainLabel: 'Started a non-preferred activity' },
  { value: 'unknown',               plainLabel: 'I didn\'t see what happened first' },
];

// Plain-English quick-pick consequences (what staff/peers did after).
export const QUICK_CONSEQUENCE_CHIPS = [
  { value: 'verbal-redirect',        plainLabel: 'Reminded them of the rule' },
  { value: 'offered-choice',         plainLabel: 'Gave them a choice' },
  { value: 'offered-break',          plainLabel: 'Offered a break' },
  { value: 'demand-removed',         plainLabel: 'Removed the task (took it away / delayed it)' },
  { value: 'given-item-activity',    plainLabel: 'Gave them what they wanted' },
  { value: 'ignored',                plainLabel: 'Planned ignoring' },
  { value: 'time-out',               plainLabel: 'Time-out / cool-down away from group' },
  { value: 'removed-from-activity',  plainLabel: 'Removed from the activity' },
  { value: 'office-referral',        plainLabel: 'Sent to the office' },
  { value: 'physical-redirect',      plainLabel: 'Physically redirected them' },
  { value: 'physical-assist',        plainLabel: 'Physical assist / safety hold' },
  { value: 'teacher-attention',      plainLabel: 'Gave 1:1 teacher attention' },
];

// Plain-English quick-pick function hypotheses. Values match
// FUNCTIONS_OF_BEHAVIOR.value.
export const QUICK_FUNCTION_CHIPS = [
  { value: 'escape',    plainLabel: 'To get out of work / a task',       icon: 'LogOut',    color: 'red'    },
  { value: 'attention', plainLabel: 'To get attention',                   icon: 'Users',     color: 'blue'   },
  { value: 'tangible',  plainLabel: 'To get something they wanted',       icon: 'Package',   color: 'amber'  },
  { value: 'sensory',   plainLabel: 'For a sensory reason (self-soothing)', icon: 'Sparkles',color: 'violet' },
  { value: 'automatic', plainLabel: 'It feels good on its own',           icon: 'Repeat',    color: 'cyan'   },
  { value: 'unknown',   plainLabel: 'I\'m not sure yet',                   icon: 'HelpCircle',color: 'gray'   },
];
